import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  LayoutDashboard,
  ClipboardList,
  Repeat,
  Users,
  Wallet,
  Banknote,
  RefreshCw,
  FileText,
  Settings,
  Package,
  MapPin,
  History,
  Building2,
  Shield,
  Star,
  BarChart3,
  Tag,
  Scale,
  ClipboardCheck,
  Clock,
  FileCheck,
} from 'lucide-react';
import DashboardLayout, { NavGroup } from './DashboardLayout';
import { PENDING_REVIEW_COUNT, INCOMPLETE_DOCUMENTS_COUNT } from '@/graphql/operations';
import { adminBottomNav } from '@/lib/navigation';

export default function AdminLayout() {
  const { t } = useTranslation('admin');

  const { data: reviewCountData } = useQuery(PENDING_REVIEW_COUNT, {
    pollInterval: 30_000,
    fetchPolicy: 'cache-and-network',
  });

  const { data: incompleteDocsData } = useQuery(INCOMPLETE_DOCUMENTS_COUNT, {
    pollInterval: 60_000,
    fetchPolicy: 'cache-and-network',
  });

  const totalPendingCount: number = (reviewCountData?.pendingReviewCount?.total as number | undefined) ?? 0;
  const incompleteDocsCount: number = (incompleteDocsData?.incompleteDocumentsCount as number | undefined) ?? 0;

  const navGroups = useMemo<NavGroup[]>(() => [
    {
      items: [
        { to: '/admin', icon: LayoutDashboard, label: t('nav.dashboard') },
      ],
    },
    {
      label: t('nav.groupTeams'),
      items: [
        { to: '/admin/aprobari', icon: ClipboardCheck, label: t('nav.approvals'), badge: totalPendingCount },
        { to: '/admin/companii', icon: Building2, label: t('nav.companies') },
        { to: '/admin/utilizatori', icon: Users, label: t('nav.users') },
        { to: '/admin/lista-asteptare', icon: Clock, label: t('nav.waitlist') },
      ],
    },
    {
      label: t('nav.groupOperations'),
      items: [
        { to: '/admin/comenzi', icon: ClipboardList, label: t('nav.bookings') },
        { to: '/admin/abonamente', icon: Repeat, label: t('nav.subscriptions') },
        { to: '/admin/recenzii', icon: Star, label: t('nav.reviews') },
        { to: '/admin/promo-coduri', icon: Tag, label: t('nav.promoCodes') },
        { to: '/admin/dispute', icon: Scale, label: t('nav.disputes') },
      ],
    },
    {
      label: t('nav.groupFinancial'),
      items: [
        { to: '/admin/plati', icon: Wallet, label: t('nav.payments') },
        { to: '/admin/viramente', icon: Banknote, label: t('nav.payouts') },
        { to: '/admin/rambursari', icon: RefreshCw, label: t('nav.refunds') },
        { to: '/admin/facturi', icon: FileText, label: t('nav.invoices') },
        { to: '/admin/documente-rezervari', icon: FileCheck, label: 'Documente', badge: incompleteDocsCount },
        { to: '/admin/rapoarte', icon: BarChart3, label: t('nav.reports') },
      ],
    },
    {
      label: t('nav.groupSystem'),
      items: [
        { to: '/admin/setari/platforma', icon: Settings, label: t('settings.tabs.platform') },
        { to: '/admin/setari/servicii', icon: Package, label: t('settings.tabs.services') },
        { to: '/admin/setari/orase', icon: MapPin, label: t('settings.tabs.cities') },
        { to: '/admin/setari/audit', icon: History, label: t('settings.tabs.audit') },
      ],
    },
  ], [t, totalPendingCount, incompleteDocsCount]);

  return (
    <DashboardLayout
      navItems={[]}
      navGroups={navGroups}
      bottomNavItems={adminBottomNav(t)}
      logoIcon={Shield}
      subtitle={t('nav.subtitle')}
      homeRoute="/admin"
    />
  );
}
